import React, { useRef, useEffect, useState } from 'react';

const RichTextEditor = ({ 
    value = '', 
    onChange, 
    placeholder = 'Enter text...', 
    height = 200,
    maxLength = 1000,
    disabled = false,
    id = 'rich-text-editor'
}) => {
    const editorRef = useRef(null);
    const [currentValue, setCurrentValue] = useState(value);
    const [plainTextLength, setPlainTextLength] = useState(0);
    const isUserTypingRef = useRef(false);

    // Set initial content when component mounts
    useEffect(() => {
        if (editorRef.current && value) {
            // Handle both plain text and HTML content
            let contentToSet = value;
            if (value && !value.includes('<')) {
                // If it's plain text, convert to HTML
                contentToSet = value.replace(/\n/g, '<br>');
            }
            
            editorRef.current.innerHTML = contentToSet;
            setCurrentValue(contentToSet);
            
            // Place cursor at the end
            setTimeout(() => {
                if (editorRef.current) {
                    const selection = window.getSelection();
                    const newRange = document.createRange();
                    newRange.selectNodeContents(editorRef.current);
                    newRange.collapse(false); // false = collapse to end
                    selection.removeAllRanges();
                    selection.addRange(newRange);
                }
            }, 0);
        }
    }, []); // Run only on mount

    // Update plain text length when content changes
    useEffect(() => {
        const plainText = currentValue.replace(/<[^>]*>/g, '');
        setPlainTextLength(plainText.length);
    }, [currentValue]);

    // Update editor content when value prop changes (only when not user typing)
    useEffect(() => {
        if (value !== currentValue && editorRef.current && !isUserTypingRef.current) {
            // Handle both plain text and HTML content
            let contentToSet = value;
            if (value && !value.includes('<')) {
                // If it's plain text, convert to HTML
                contentToSet = value.replace(/\n/g, '<br>');
            }
            
            // Update content
            editorRef.current.innerHTML = contentToSet;
            setCurrentValue(contentToSet);
            
            // Always place cursor at the end when content is updated
            setTimeout(() => {
                if (editorRef.current) {
                    const selection = window.getSelection();
                    const newRange = document.createRange();
                    newRange.selectNodeContents(editorRef.current);
                    newRange.collapse(false); // false = collapse to end
                    selection.removeAllRanges();
                    selection.addRange(newRange);
                }
            }, 0);
        }
    }, [value, currentValue]);

    const handleInput = () => {
        if (!editorRef.current) return;
        
        // Set flag to indicate user is typing
        isUserTypingRef.current = true;
        
        const content = editorRef.current.innerHTML;
        const plainText = content.replace(/<[^>]*>/g, '');
        
        // Check character limit
        if (plainText.length > maxLength) {
            // Save cursor position before truncating
            const selection = window.getSelection();
            const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
            
            // Truncate content if it exceeds limit
            const truncatedContent = content.substring(0, maxLength);
            editorRef.current.innerHTML = truncatedContent;
            setCurrentValue(truncatedContent);
            
            // Restore cursor position
            if (range) {
                try {
                    selection.removeAllRanges();
                    selection.addRange(range);
                } catch (e) {
                    // Place cursor at end if restoration fails
                    const newRange = document.createRange();
                    newRange.selectNodeContents(editorRef.current);
                    newRange.collapse(false);
                    selection.removeAllRanges();
                    selection.addRange(newRange);
                }
            }
            
            if (onChange) {
                onChange(truncatedContent);
            }
        } else {
            setCurrentValue(content);
            if (onChange) {
                onChange(content);
            }
        }
        
        // Reset flag after a short delay
        setTimeout(() => {
            isUserTypingRef.current = false;
        }, 100);
    };

    const handleKeyDown = (e) => {
        const plainText = editorRef.current?.innerHTML.replace(/<[^>]*>/g, '') || '';
        
        // Prevent typing if at character limit (allow backspace and delete)
        if (plainText.length >= maxLength && e.keyCode !== 8 && e.keyCode !== 46) {
            e.preventDefault();
        }
    };

    const execCommand = (command, value = null) => {
        if (editorRef.current) {
            editorRef.current.focus();
            document.execCommand(command, false, value);
            // Use setTimeout to ensure the command is executed before calling handleInput
            setTimeout(handleInput, 0);
        }
    };

    const insertList = (type) => {
        if (editorRef.current) {
            editorRef.current.focus();
            // Check if we're already in a list of this type
            const isInList = document.queryCommandState(type === 'ordered' ? 'insertOrderedList' : 'insertUnorderedList');
            if (isInList) {
                // If already in list, remove it
                document.execCommand('removeFormat', false);
            } else {
                // Insert the list
                document.execCommand(type === 'ordered' ? 'insertOrderedList' : 'insertUnorderedList', false);
            }
            setTimeout(handleInput, 0);
        }
    };

    const insertHeading = (level) => {
        if (editorRef.current) {
            editorRef.current.focus();
            // Check if we're already in this heading level
            const currentFormat = document.queryCommandValue('formatBlock');
            if (currentFormat === `h${level}`) {
                // If already this heading, convert to paragraph
                document.execCommand('formatBlock', false, 'p');
            } else {
                // Set the heading
                document.execCommand('formatBlock', false, `h${level}`);
            }
            setTimeout(handleInput, 0);
        }
    };

    const removeFormatting = () => {
        if (editorRef.current) {
            editorRef.current.focus();
            document.execCommand('removeFormat', false);
            // Also remove any block formatting
            document.execCommand('formatBlock', false, 'p');
            setTimeout(handleInput, 0);
        }
    };

    const insertLineBreak = () => {
        if (editorRef.current) {
            editorRef.current.focus();
            document.execCommand('insertHTML', false, '<br>');
            setTimeout(handleInput, 0);
        }
    };

    const removeLineBreak = () => {
        if (editorRef.current) {
            editorRef.current.focus();
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                const container = range.commonAncestorContainer;
                
                // Check if we're at a line break
                if (container.nodeType === Node.TEXT_NODE) {
                    const text = container.textContent;
                    const offset = range.startOffset;
                    
                    // Look for line breaks around cursor
                    if (text[offset] === '\n' || text[offset - 1] === '\n') {
                        // Remove the line break
                        const newText = text.replace(/\n/g, '');
                        container.textContent = newText;
                        range.setStart(container, Math.min(offset, newText.length));
                        range.collapse(true);
                        selection.removeAllRanges();
                        selection.addRange(range);
                    }
                } else {
                    // Look for <br> tags
                    const brElements = container.querySelectorAll('br');
                    if (brElements.length > 0) {
                        brElements[0].remove();
                    }
                }
            }
            setTimeout(handleInput, 0);
        }
    };

    const ToolbarButton = ({ onClick, children, title, active = false }) => (
        <button
            type="button"
            className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={onClick}
            title={title}
            disabled={disabled}
            style={{ margin: '2px' }}
        >
            {children}
        </button>
    );

    return (
        <div className="rich-text-editor position-relative">
            {/* Toolbar */}
            <div className="toolbar border rounded-top p-2 bg-light" style={{ fontSize: '14px' }}>
                <div className="d-flex flex-wrap align-items-center">
                    {/* Text Formatting */}
                    <ToolbarButton onClick={() => execCommand('bold')} title="Bold">
                        <strong>B</strong>
                    </ToolbarButton>
                    <ToolbarButton onClick={() => execCommand('italic')} title="Italic">
                        <em>I</em>
                    </ToolbarButton>
                    <ToolbarButton onClick={() => execCommand('underline')} title="Underline">
                        <u>U</u>
                    </ToolbarButton>
                    
                    <div className="vr mx-2"></div>
                    
                    {/* Headings */}
                    <ToolbarButton onClick={() => insertHeading(1)} title="Heading 1 (click again to remove)">
                        H1
                    </ToolbarButton>
                    <ToolbarButton onClick={() => insertHeading(2)} title="Heading 2 (click again to remove)">
                        H2
                    </ToolbarButton>
                    <ToolbarButton onClick={() => insertHeading(3)} title="Heading 3 (click again to remove)">
                        H3
                    </ToolbarButton>
                    
                    <div className="vr mx-2"></div>
                    
                    {/* Lists */}
                    <ToolbarButton onClick={() => insertList('unordered')} title="Bullet List (click again to remove)">
                        • List
                    </ToolbarButton>
                    <ToolbarButton onClick={() => insertList('ordered')} title="Numbered List (click again to remove)">
                        1. List
                    </ToolbarButton>
                    
                    <div className="vr mx-2"></div>
                    
                    {/* Alignment */}
                    <ToolbarButton onClick={() => execCommand('justifyLeft')} title="Align Left">
                        ⬅
                    </ToolbarButton>
                    <ToolbarButton onClick={() => execCommand('justifyCenter')} title="Align Center">
                        ↔
                    </ToolbarButton>
                    <ToolbarButton onClick={() => execCommand('justifyRight')} title="Align Right">
                        ➡
                    </ToolbarButton>
                    
                    <div className="vr mx-2"></div>
                    
                    {/* Other */}
                    <ToolbarButton onClick={insertLineBreak} title="Insert Line Break">
                        ↵
                    </ToolbarButton>
                    <ToolbarButton onClick={removeLineBreak} title="Remove Line Break">
                        ⌫
                    </ToolbarButton>
                    <ToolbarButton onClick={() => execCommand('insertHorizontalRule')} title="Insert Horizontal Line">
                        ─
                    </ToolbarButton>
                    <ToolbarButton onClick={removeFormatting} title="Remove All Formatting">
                        Clear
                    </ToolbarButton>
                </div>
            </div>
            
            {/* Editor */}
            <div className="position-relative">
                <div
                    ref={editorRef}
                    contentEditable={!disabled}
                    className="form-control border-top-0 rounded-bottom"
                    style={{
                        minHeight: `${height}px`,
                        maxHeight: `${height}px`,
                        overflowY: 'auto',
                        padding: '12px',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        outline: 'none'
                    }}
                    onInput={handleInput}
                    onKeyDown={handleKeyDown}
                    onPaste={(e) => {
                        // Allow paste but handle it in handleInput
                        setTimeout(handleInput, 0);
                    }}
                    suppressContentEditableWarning={true}
                />
                
                {/* Placeholder */}
                {!currentValue && (
                    <div
                        className="text-muted position-absolute"
                        style={{
                            top: '12px',
                            left: '12px',
                            pointerEvents: 'none',
                            fontSize: '14px',
                            color: '#6c757d',
                            zIndex: 1
                        }}
                    >
                        {placeholder}
                    </div>
                )}
            </div>
            
            {/* Character Count */}
            <div className="form-text mt-1">
                {plainTextLength}/{maxLength} characters
            </div>
        </div>
    );
};

export default RichTextEditor;
